import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowLeft, Check, Clock, X } from 'lucide-react';

import { trackEvent } from '../../utils/gt';
import { prefersReducedMotion } from '../../utils/motion';
import { useResolvedTheme } from '../../utils/theme';

import { TurnstileField } from '../TurnstileField';
import { AUTH_EXERCISES, pickAuthExercise, shouldPlayExerciseVideo, sideExercises } from './authExercises';
import { ForgotForm, ForgotSent, LoginForm, ServerAlert, SignupForm, SignupSent } from './AuthForms';
import { useAuthRequests } from './useAuthRequests';
import { useCookieCardRoom, useVisibleViewport } from './useAuthViewport';
import logo from '../../../assets/plates/nav-logo.png';

import '../../styles/pages/auth.css';
import { PreviewArt } from '../app/PreviewArt';

// Phones get the bottom sheet; from 900px the exercise panel sits beside the form (between, the form alone)
const PHONE_QUERY = '(max-width: 719px)';
const ART_QUERY = '(min-width: 900px)';
// A visible height under this on a phone means the keyboard is up (or the screen is that short): the exercise row and
// the subline step aside so the button stays in reach
const SHORT_VIEW_PX = 520;
const KEYBOARD_SETTLE_MS = 320;
const ENTRY_MODES = ['login', 'signup'];

// Phones and tablets: opening the window shouldn't pop the keyboard over it
const prefersTouch = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

// The sheet rises from the bottom on phones; the dialog fades and settles. Reduced motion only fades (NextUI's own
// slide stays stuck 80px down there)
const motionFor = (isPhone) => {
  const still = !isPhone || prefersReducedMotion();
  return {
    variants: {
      enter: { opacity: 1, y: 0, scale: 1, transition: { duration: still ? 0.22 : 0.32, ease: [0.16, 1, 0.3, 1] } },
      exit: { opacity: 0, y: still ? 0 : 48, scale: 1, transition: { duration: 0.16, ease: 'easeIn' } },
    },
  };
};

const Brand = ({ name }) => (
  <span className="mt-ad-brand">
    <img src={logo} alt="" />
    {name}
  </span>
);

// The window's exercise: its execution video (muted, looping), or its picture when the video isn't welcome
const ExerciseClip = ({ exercise }) => {
  const [canPlay] = useState(shouldPlayExerciseVideo);
  const [failed, setFailed] = useState(false);
  if (!canPlay || failed) return <img className="mt-ad-media" src={exercise.thumbnailUrl} alt="" />;
  return (
    <video
      key={exercise.id}
      className="mt-ad-media"
      src={exercise.videoUrl}
      poster={exercise.thumbnailUrl}
      muted
      loop
      autoPlay
      playsInline
      disablePictureInPicture
      preload="auto"
      aria-hidden="true"
      tabIndex={-1}
      onError={() => setFailed(true)}
    />
  );
};

const useExerciseText = (exercise) => {
  const { t, i18n } = useTranslation();
  const isPt = (i18n.language || 'pt').startsWith('pt');
  return {
    name: isPt ? exercise.titlePt : exercise.title,
    meta: `${t(`exerciseFilters.muscleGroups.${exercise.muscle}`)} · ${t(`exerciseFilters.equipmentCategories.${exercise.equipment}`)}`,
  };
};

const ExerciseCredit = ({ exercise }) => {
  const { name, meta } = useExerciseText(exercise);
  return (
    <p className="mt-ad-credit">
      <strong>{name}</strong>
      <span>{meta}</span>
    </p>
  );
};

// Desktop, beside sign-in and the recovery steps: one library exercise playing, as OdeParfum shows the window's perfume
const LoginArt = ({ exercise, copy }) => (
  <div className="mt-ad-art">
    <div className="mt-ad-tile mt-ad-tile--wide">
      <ExerciseClip exercise={exercise} />
    </div>
    <ExerciseCredit exercise={exercise} />
    <p className="mt-ad-art__note">{copy('art.note')}</p>
  </div>
);

// Desktop, beside sign-up: three exercises (the window's in the middle, playing) and what the account gives
const SignupArt = ({ exercise, copy }) => {
  const [left, right] = sideExercises(exercise);
  return (
    <div className="mt-ad-art mt-ad-art--signup">
      <div>
        <div className="mt-ad-tiles" aria-hidden="true">
          <div className="mt-ad-tile">
            <img className="mt-ad-media" src={left.thumbnailUrl} alt="" />
          </div>
          <div className="mt-ad-tile mt-ad-tile--main">
            <ExerciseClip exercise={exercise} />
          </div>
          <div className="mt-ad-tile">
            <img className="mt-ad-media" src={right.thumbnailUrl} alt="" />
          </div>
        </div>
        <ExerciseCredit exercise={exercise} />
      </div>
      <ul className="mt-ad-perks">
        {['library', 'guided', 'alexa'].map((perk) => (
          <li key={perk}>
            <Check aria-hidden="true" />
            <span>{copy(`art.perks.${perk}`)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-ad-pro">
        <strong>{copy('art.proLead')}</strong> {copy('art.proText')}
      </p>
    </div>
  );
};

// Phones, above the sign-in and sign-up forms: the same exercise in one row (a picture: no video on phones)
const ExerciseRow = ({ exercise, copy }) => {
  const { name, meta } = useExerciseText(exercise);
  return (
    <div className="mt-ad-compact">
      <div className="mt-ad-tile mt-ad-tile--compact" aria-hidden="true">
        <img className="mt-ad-media" src={exercise.thumbnailUrl} alt="" />
      </div>
      <p className="mt-ad-credit mt-ad-credit--row">
        <strong>{name}</strong>
        <span>
          {meta} · {copy('art.withVideo')}
        </span>
      </p>
    </div>
  );
};

/**
 * One window for sign in, sign up and password recovery (A · Vitrine, chosen 29/10/2026), switching its content in
 * place. Desktop: a dialog with a gray panel playing a real library exercise beside the form (sign-up widens and lists
 * what the account gives). Phones: a bottom sheet with "Entrar | Criar conta" tabs in a fixed head, lifted above the
 * keyboard. Errors show inside the form above its button; the window can't close while a request is out. It follows the
 * page's theme (the device's, or the one picked in Conta), on the public pages and over the app alike.
 */
export const AuthDialog = ({ isOpen, mode, afterSignupPath, expired, onModeChange, onClose }) => {
  const { t } = useTranslation();
  const copy = useCallback((key, options) => t(`components.AuthDialog.${key}`, options), [t]);
  const navigate = useNavigate();
  const { search } = useLocation();
  // Expired sessions land on /login/<the page they were on>, which can be several segments deep
  const params = useParams();
  const redirectUrl = params['*'] || params.redirectUrl;
  const isPhone = useMediaQuery({ query: PHONE_QUERY });
  const showArt = useMediaQuery({ query: ART_QUERY });
  const theme = useResolvedTheme();

  const [exercise, setExercise] = useState(AUTH_EXERCISES[0]);
  const sheetBodyRef = useRef(null);
  const wasOpenRef = useRef(false);
  const lastExerciseRef = useRef(null);

  const {
    busy,
    error,
    badField,
    setBadField,
    sentTo,
    resetCooldown,
    confirmCooldown,
    emailRef,
    turnstileRef,
    start,
    clearErrors,
    handleLogin,
    handleSignup,
    requestReset,
    resendConfirmation,
  } = useAuthRequests({
    afterSignupPath,
    onLoggedIn: ({ fromSignupLink }) => {
      onClose();
      navigate(redirectUrl ? `/${redirectUrl}${search}` : fromSignupLink ? '/workout' : '/dashboard');
    },
    onSignedUp: () => {
      toast(t('notifications.signupSuccess'));
      onClose();
      navigate(afterSignupPath || '/dashboard?w=true');
    },
    onSignupSent: () => onModeChange('signupSent'),
    onResetSent: () => onModeChange('forgotSent'),
  });

  // Each opening: a fresh window and another exercise (decided before paint, so the first frame already shows it)
  useLayoutEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const pick = pickAuthExercise(lastExerciseRef.current);
      lastExerciseRef.current = pick.id;
      setExercise(pick);
      start();
      if (expired) trackEvent('modal_login_expired', 'Modal Login Expired', 'modal_login', true);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, expired, start]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'login') trackEvent('modal_login_open', 'Modal Login Open', 'modal_login', true);
    if (mode === 'signup') trackEvent('modal_signup_open', 'Modal Signup Open', 'modal_signup', true);
  }, [isOpen, mode]);

  // Phones: the keyboard and the cookie card
  const view = useVisibleViewport(isOpen && isPhone);
  const cookieRoom = useCookieCardRoom(isOpen);
  const keyboardUp = isPhone && (view.inset > 0 || (view.height != null && view.height < SHORT_VIEW_PX));

  useEffect(() => {
    if (!isOpen) return undefined;
    const root = document.documentElement;
    root.classList.add('mt-ad-open');
    return () => root.classList.remove('mt-ad-open');
  }, [isOpen]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('mt-ad-kb', isOpen && keyboardUp);
    return () => root.classList.remove('mt-ad-kb');
  }, [isOpen, keyboardUp]);

  // The cookie card's room (plus its 24px from the edge), kept free by the window: see auth.css
  useEffect(() => {
    const root = document.documentElement;
    if (!isOpen || !cookieRoom) return undefined;
    root.style.setProperty('--mt-ad-cookie', `${cookieRoom + 24}px`);
    return () => root.style.removeProperty('--mt-ad-cookie');
  }, [isOpen, cookieRoom]);

  const close = () => {
    if (!busy) onClose();
  };

  const switchMode = (next) => {
    if (busy) return;
    clearErrors();
    onModeChange(next);
  };

  const isEntry = ENTRY_MODES.includes(mode);
  const titles = {
    login: copy('login.title'),
    signup: copy('signup.title'),
    forgot: copy('forgot.title'),
    forgotSent: copy('forgotSent.title'),
    signupSent: copy('signupSent.title'),
  };
  const subs = { login: copy('login.sub'), signup: copy('signup.sub'), forgot: copy('forgot.sub') };

  const alert = error && (
    <ServerAlert
      kind={error.kind}
      action={error.action}
      copy={copy}
      email={emailRef.current}
      onForgot={() => switchMode('forgot')}
      onLogin={() => switchMode('login')}
      resend={{ onClick: () => resendConfirmation(mode), cooldown: confirmCooldown }}
    />
  );
  const turnstile = <TurnstileField ref={turnstileRef} action={mode} theme={theme} />;
  const shared = {
    copy,
    t,
    busy,
    badField,
    alert,
    defaultEmail: emailRef.current,
    autoFocus: !prefersTouch(),
    onEmailChange: (value) => {
      emailRef.current = value;
    },
    onFieldEdit: (field) => setBadField((current) => (current === field ? null : current)),
  };

  const forms = {
    login: (
      <LoginForm
        key="login"
        {...shared}
        turnstile={turnstile}
        foot={!isPhone}
        notice={
          expired && (
            <div className="mt-ad-alert mt-ad-alert--notice" role="status">
              <Clock aria-hidden="true" />
              <p>{copy('login.expired')}</p>
            </div>
          )
        }
        onSubmit={handleLogin}
        onForgot={() => switchMode('forgot')}
        onSignup={() => switchMode('signup')}
      />
    ),
    signup: <SignupForm key="signup" {...shared} turnstile={turnstile} foot={!isPhone} onSubmit={handleSignup} onLogin={() => switchMode('login')} />,
    forgot: (
      <ForgotForm key="forgot" {...shared} cooldown={resetCooldown} onSubmit={({ email }) => requestReset(email.trim())} onLogin={() => switchMode('login')} />
    ),
    forgotSent: (
      <ForgotSent
        key="forgotSent"
        copy={copy}
        busy={busy}
        email={sentTo}
        cooldown={resetCooldown}
        alert={alert}
        onLogin={() => switchMode('login')}
        onResend={() => requestReset(sentTo)}
        onChangeEmail={() => switchMode('forgot')}
      />
    ),
    signupSent: (
      <SignupSent
        key="signupSent"
        copy={copy}
        email={sentTo}
        cooldown={confirmCooldown}
        alert={alert}
        onLogin={() => switchMode('login')}
        onResend={() => resendConfirmation(mode)}
      />
    ),
  };

  const head = (
    <header className="mt-ad-head">
      {(mode === 'forgotSent' || mode === 'signupSent') && (
        <PreviewArt kind="mail" size="sm" />
      )}
      <h2 className="mt-ad-title">{titles[mode]}</h2>
      {subs[mode] && <p className="mt-ad-sub">{subs[mode]}</p>}
    </header>
  );

  const closeButton = (
    <button type="button" className="mt-ad-icon mt-ad-close" onClick={close} disabled={!!busy} aria-label={t('standalone.close')}>
      <X aria-hidden="true" />
    </button>
  );

  const backButton = (
    <button type="button" className="mt-ad-back" onClick={() => switchMode('login')} disabled={!!busy}>
      <ArrowLeft aria-hidden="true" />
      {copy('backToLogin')}
    </button>
  );

  // Arrow keys move between the two tabs (and switch, as a click does)
  const onTabKeyDown = (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 'login' : event.key === 'End' ? 'signup' : mode === 'login' ? 'signup' : 'login';
    switchMode(next);
    requestAnimationFrame(() => document.getElementById(`mt-ad-tab-${next}`)?.focus());
  };

  const tabs = (
    <div className="mt-ad-tabs" role="tablist" aria-label={copy('tabsLabel')}>
      {ENTRY_MODES.map((entry) => (
        <button
          key={entry}
          type="button"
          role="tab"
          id={`mt-ad-tab-${entry}`}
          className="mt-ad-tab"
          aria-selected={mode === entry}
          aria-controls="mt-ad-panel"
          tabIndex={mode === entry ? 0 : -1}
          disabled={!!busy && mode !== entry}
          onClick={() => switchMode(entry)}
          onKeyDown={onTabKeyDown}
        >
          {copy(entry === 'login' ? 'tabLogin' : 'tabSignup')}
        </button>
      ))}
    </div>
  );

  // With the keyboard up, the field being typed in scrolls into the room left above it, once the keyboard settles
  const revealField = (event) => {
    const field = event.target;
    if (!isPhone || !field.matches?.('input:not([type="checkbox"])')) return;
    window.setTimeout(() => {
      if (document.activeElement === field) field.scrollIntoView({ block: 'nearest' });
    }, KEYBOARD_SETTLE_MS);
  };

  const sheet = (
    <>
      <div className="mt-ad-sheet-head">
        <span className="mt-ad-grab" aria-hidden="true" />
        <div className="mt-ad-bar">
          {isEntry ? <Brand name={t('appName')} /> : backButton}
          {closeButton}
        </div>
        {isEntry && tabs}
      </div>
      <div
        ref={sheetBodyRef}
        className="mt-ad-sheet-body"
        id="mt-ad-panel"
        role={isEntry ? 'tabpanel' : undefined}
        aria-labelledby={isEntry ? `mt-ad-tab-${mode}` : undefined}
        onFocus={revealField}
      >
        {isEntry && <ExerciseRow exercise={exercise} copy={copy} />}
        <div className="mt-ad-swap" key={mode}>
          {head}
          {forms[mode]}
        </div>
      </div>
    </>
  );

  const dialog = (
    <div className="mt-ad-grid">
      {showArt && (mode === 'signup' ? <SignupArt exercise={exercise} copy={copy} /> : <LoginArt exercise={exercise} copy={copy} />)}
      <div className="mt-ad-main">
        {closeButton}
        <div className="mt-ad-swap" key={mode}>
          {mode === 'login' && <Brand name={t('appName')} />}
          {!isEntry && <div>{backButton}</div>}
          {head}
          {forms[mode]}
        </div>
      </div>
    </div>
  );

  // Phones: above the keyboard (iOS keeps a fixed sheet under it), as tall as what's left, and clear of the cookie card
  // at the top while it waits for an answer (it steps aside while the keyboard is up)
  const topRoom = cookieRoom && !keyboardUp ? cookieRoom + 24 : 0;
  const gap = (keyboardUp ? 8 : 24) + topRoom;
  const sheetStyle = isPhone
    ? {
        marginBottom: view.inset ? `${view.inset}px` : undefined,
        maxHeight:
          view.height != null ? `min(calc(100dvh - ${gap + view.inset}px), ${Math.max(0, view.height - gap)}px)` : `calc(100dvh - ${gap}px)`,
      }
    : undefined;

  const classes = [
    'mt-root',
    'mt-ad',
    isPhone ? 'mt-ad--sheet' : 'mt-ad--dialog',
    mode === 'signup' && showArt && !isPhone ? 'mt-ad--wide' : '',
    !showArt && !isPhone ? 'mt-ad--solo' : '',
    keyboardUp ? 'mt-ad--kb' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && close()}
      placement={isPhone ? 'bottom' : 'center'}
      hideCloseButton
      isDismissable={!busy}
      isKeyboardDismissDisabled={!!busy}
      motionProps={motionFor(isPhone)}
      // NextUI ignores aria-labelledby without its own header
      aria-label={titles[mode]}
      style={sheetStyle}
      classNames={{
        wrapper: 'mt-ad-wrap',
        base: classes,
        backdrop: 'mt-ad-backdrop',
      }}
    >
      <ModalContent>{() => (isPhone ? sheet : dialog)}</ModalContent>
    </Modal>
  );
};
